"use client";

import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import type { NumberFormatter } from "@/lib/tool-kit/physics-waves/format";
import {
  MATERIAL_INDEX,
  MATERIAL_ORDER,
  type MaterialId,
} from "@/lib/tool-kit/physics-waves/refraction";

export type MediumChoice = MaterialId | "custom";

export interface MediumState {
  id: MediumChoice;
  custom: string;
}

export interface MediumStrings {
  materials: Record<MaterialId, string>;
  customOption: string;
  customLabel: string;
  optionLabel: string;
}

/** The refractive index a medium choice stands for, or null for an unreadable custom value. */
export function mediumIndex(medium: MediumState): number | null {
  return medium.id === "custom"
    ? parseNumberInput(medium.custom)
    : MATERIAL_INDEX[medium.id];
}

/** A material menu with a "your own value" option that reveals a number box. */
export function MediumField({
  id,
  label,
  medium,
  onChange,
  strings,
  fmt,
}: {
  id: string;
  label: string;
  medium: MediumState;
  onChange: (medium: MediumState) => void;
  strings: MediumStrings;
  fmt: NumberFormatter;
}) {
  const options = [
    ...MATERIAL_ORDER.map((material) => ({
      value: material as MediumChoice,
      label: t(strings.optionLabel, {
        name: strings.materials[material],
        n: fmt(MATERIAL_INDEX[material], { maximumFractionDigits: 6 }),
      }),
    })),
    { value: "custom" as MediumChoice, label: strings.customOption },
  ];
  return (
    <div className="space-y-3">
      <SelectField
        id={id}
        label={label}
        onChange={(next) => onChange({ ...medium, id: next })}
        options={options}
        value={medium.id}
      />
      {medium.id === "custom" ? (
        <TextField
          id={`${id}-custom`}
          label={strings.customLabel}
          numeric
          onChange={(custom) => onChange({ ...medium, custom })}
          value={medium.custom}
        />
      ) : null}
    </div>
  );
}
